'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AssetType, getAssets } from '@vimond/services/content-discovery';
import {
  deleteVideoProgress,
  getVidoesInProgress,
  ViewingHistoryType,
} from '@vimond/services/viewing-history';
import { useEffect, useState } from 'react';
import { useAuthentication } from '../../authentication';
import { useSubprofiles } from '../../subprofiles';

type UseResumeWatching = {
  viewingProgress: ViewingHistoryType[] | undefined;
  assets: AssetType[] | undefined;
  isLoadingViewingProgress: boolean;
  viewingProgressError: Error | null;
  deleteVideoProgress: (assetId: string) => Promise<void | Response>;
};

export const useResumeWatching = (locale?: string): UseResumeWatching => {
  const { jwt } = useAuthentication();
  const { subprofileToken, activeSubprofile } = useSubprofiles();
  const [assets, setAssets] = useState<AssetType[] | undefined>([]);

  const queryClient = useQueryClient();

  const cacheKey = ['progress', activeSubprofile?.subProfileId];

  const {
    data: viewingProgress,
    error: viewingProgressError,
    isLoading: isLoadingViewingProgress,
  } = useQuery({
    queryKey: cacheKey,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!jwt || !subprofileToken) return [];
      const progress = await getVidoesInProgress({ jwt, subprofileToken });
      if (!progress?.length) return [];
      const assetIds =
        progress?.map((asset) => {
          return asset.assetId;
        }) ?? [];
      const assets = await getAssets({ assetIds, locale });
      setAssets(assets);
      return progress;
    },
    enabled: !!jwt && !!subprofileToken,
  });

  // create mutation for deleting video progress
  const deleteVideoProgressMutation = useMutation({
    mutationFn: async (assetId: string) => {
      if (!jwt || !subprofileToken) return;
      return deleteVideoProgress({ jwt, subprofileToken, assetId });
    },
    onSuccess: (_data, variables) => {
      // remove deleted asset from cache
      const list = queryClient.getQueryData<ViewingHistoryType[]>(cacheKey)!;
      const updatedCache = list.filter((entry) => entry.assetId !== variables);
      queryClient.setQueryData(cacheKey, updatedCache);
    },
  });

  // invalidate cache when subprofile changes
  useEffect(() => {
    const invalidateCache = async () => {
      await queryClient.invalidateQueries({ queryKey: ['progress'] });
      setAssets(undefined);
    };
    void invalidateCache();
  }, [subprofileToken, queryClient]);

  return {
    viewingProgress,
    assets,
    isLoadingViewingProgress,
    viewingProgressError,
    deleteVideoProgress: deleteVideoProgressMutation.mutateAsync,
  };
};
